import { FilterX } from 'lucide-react';
import {
  hasActiveRoutineFilters,
  NO_ROUTINE_FILTERS,
  offersAChoice,
  type FilterOption,
  type RoutineFilterState,
} from '../../../lib/routine-filters';
import { useTranslation } from '../../../lib/i18n';
import { Button, OverflowMenu, type OverflowMenuAction } from '../ui';
import styles from './RoutinesFilterBar.legacy.module.css';

// The Routines list's filter row: **Space** — a labelled DS picker whose label states the current value —
// plus a Clear that appears once something is narrowed. It carried an Agent picker too until that axis was
// dropped from this screen by design; `TasksFilterBar` still has both, so the two rows no longer match.
//
// The options are **passed in, not derived here**: the host clamps its state against the same list
// (`clampRoutineFilters`), and a list derived twice is a list that can disagree with itself. What the bar
// owns is the rule that a control with nothing to choose between is not drawn at all (`offersAChoice`).
export function RoutinesFilterBar({
  spaces,
  filters,
  onChange,
  onBi,
  className,
}: {
  /** The Spaces worth offering — `spacesOf(routines, workspaces)`. Empty when the host fixes this axis. */
  spaces: FilterOption[];
  filters: RoutineFilterState;
  onChange: (next: RoutineFilterState) => void;
  /** Reports which filter was used, for BI. */
  onBi?: (name: string) => void;
  /** Layout from the enclosing row — the bar owns its chips, not its place on the page. */
  className?: string;
}) {
  const { t } = useTranslation();

  const pick = (name: string, next: RoutineFilterState) => {
    onBi?.(name);
    onChange(next);
  };

  const spaceActions: OverflowMenuAction[] = [
    {
      key: 'all',
      label: t('routines.filter.allSpaces'),
      onSelect: () => pick('filter_space_all', { ...filters, workspaceId: '' }),
    },
    ...spaces.map((space) => ({
      key: space.id,
      label: space.name,
      onSelect: () => pick('filter_space', { ...filters, workspaceId: space.id }),
    })),
  ];

  const spaceLabel = spaces.find((space) => space.id === filters.workspaceId)?.name ?? t('routines.filter.allSpaces');

  return (
    <div className={className ? `${styles.bar} ${className}` : styles.bar}>
      {offersAChoice(spaces) && (
        <OverflowMenu
          label={spaceLabel}
          ariaLabel={t('packs.section.workspace')}
          actions={spaceActions}
          selectedKey={filters.workspaceId || 'all'}
          onOpen={() => onBi?.('filter_space_open')}
          triggerClassName={styles.chip}
        />
      )}
      {hasActiveRoutineFilters(filters) && (
        <Button
          variant="text"
          size="small"
          startIcon={<FilterX size={16} aria-hidden />}
          onClick={() => pick('filter_clear', NO_ROUTINE_FILTERS)}
        >
          {t('files.filter.clear')}
        </Button>
      )}
    </div>
  );
}
